import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    ButtonAtom,
    DropdownAtom,
    TextFieldAtom,
} from '../../../components/atoms/atom';
import LV_STATUS from '../../../constants/lv-status';
import {
    formatTicketCategoriesForDropdown,
    formatTicketCategoriesForNachtrag,
    useGetProjectTicketCategoriesWithLVStatusQuery,
} from '../../../store/services/ticketCategoriesService';

// Move initialFormState outside the component
const initialFormState = {
    name: "",
    file: null,
    projectTicketCategoryId: "",
    isNachtraege: false,
    statusCode: LV_STATUS.DRAFT,
    cost: "",
};

const AddLVForm = forwardRef(function AddLVForm({
    projectId,
    onSubmit,
    loading = false,
    mode = 'add',
    initialData = null,
    onCancel = () => { },
    isLocalProject = false,
    hideButtons = false,
}, ref) {
    const { t } = useTranslation();

    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState({});

    const {
        data: rawCategoriesResponse,
        isLoading: isLoadingCategories,
    } = useGetProjectTicketCategoriesWithLVStatusQuery(projectId, {
        skip: !projectId,
    });
    const rawCategories = useMemo(
        () => rawCategoriesResponse?.items ?? [],
        [rawCategoriesResponse?.items],
    );
    const ticketCategoriesForMainLV = useMemo(
        () => formatTicketCategoriesForDropdown(rawCategories),
        [rawCategories],
    );
    const ticketCategoriesForNachtrag = useMemo(
        () => formatTicketCategoriesForNachtrag(rawCategories),
        [rawCategories],
    );
    const ticketCategories = formData.isNachtraege ? ticketCategoriesForNachtrag : ticketCategoriesForMainLV;

    const isUploadMode = mode === 'upload';
    const isEditMode = mode === 'edit';
    const uploadOnly = isUploadMode;

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name || '',
                projectTicketCategoryId: initialData.projectTicketCategoryId || '',
                file: null,
                isNachtraege: initialData.isNachtraege || false,
                statusCode: initialData.statusCode || LV_STATUS.DRAFT,
                cost: initialData.cost || '',
            });
        }
    }, [initialData]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                file: file
            }));

            // Clear file error
            if (errors.file) {
                setErrors(prev => ({
                    ...prev,
                    file: ''
                }));
            }
        }
    };

    const validateForm = useCallback(() => {
        const newErrors = {};

        if (!uploadOnly && !formData.name.trim()) {
            newErrors.name = 'lv.error-lv-name-required';
        }

        // File is required only in upload mode; optional when creating any LV
        if (!formData.file && uploadOnly) {
            newErrors.file = 'lv.error-gaeb-file-required';
        }

        if (!uploadOnly && !formData.projectTicketCategoryId) {
            newErrors.projectTicketCategoryId = "lv.error-category-required";
        }

        // Validate Nachtrag-specific fields
        if (!uploadOnly && formData.isNachtraege) {
            if (!formData.statusCode) {
                newErrors.statusCode = 'lv.error-status-required';
            }
            if (formData.cost === "" || formData.cost === null || formData.cost === undefined || parseFloat(formData.cost) < 0) {
                newErrors.cost = 'lv.error-cost-required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [uploadOnly, formData]);

    const handleSubmit = useCallback((event) => {
        if (event?.preventDefault) event.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Create FormData for file upload
        const submitData = new FormData();
        if (!uploadOnly && !isEditMode) {
            submitData.append('name', formData.name);
            submitData.append('projectTicketCategoryId', formData.projectTicketCategoryId);

            // Add Nachtrag-specific fields
            if (formData.isNachtraege) {
                submitData.append('isNachtraege', 'true');
                submitData.append('statusCode', formData.statusCode);
                submitData.append('cost', formData.cost);
            }
        } else if (isEditMode) {
            // For edit mode, append all Nachtrag fields
            submitData.append('name', formData.name);
            submitData.append('projectTicketCategoryId', formData.projectTicketCategoryId);
            submitData.append('statusCode', formData.statusCode);
            submitData.append('cost', formData.cost);
        } else if (uploadOnly && initialData) {
            submitData.append("lvId", initialData.id);
        }

        if (formData.file) {
            submitData.append('gaebFile', formData.file);
        }

        onSubmit(submitData);
    }, [validateForm, formData, uploadOnly, isEditMode, initialData, onSubmit]);

    useImperativeHandle(ref, () => ({ triggerSubmit: handleSubmit }), [handleSubmit]);

    // Prepare status options for dropdown
    const statusOptions = Object.keys(LV_STATUS).map(key => ({
        label: t(`lv.status.${key.toLowerCase().replace('_', '-')}`),
        value: LV_STATUS[key]
    }));

    return (
        <Container>
            {/* Show LV Type toggle for all projects, not in upload/edit mode */}
            {!uploadOnly && !isEditMode && (
                <Row className="mb-3">
                    <Col>
                        <Form.Check
                            type="switch"
                            id="isNachtraege-switch"
                            label={t("lv.is-nachtrag")}
                            checked={formData.isNachtraege}
                            onChange={(e) =>
                                handleInputChange("isNachtraege", e.target.checked)
                            }
                            disabled={loading}
                        />
                    </Col>
                </Row>
            )}

            <Row className="mb-3">
                <Col>
                    <TextFieldAtom
                        type="text"
                        label={t("lv.lv-name")}
                        placeholder={t("lv.lv-name")}
                        required={!uploadOnly}
                        value={formData.name}
                        error={errors.name ? t(errors.name) : ""}
                        onChange={(e) =>
                            handleInputChange("name", e.target.value)
                        }
                        disabled={uploadOnly || loading}
                    />
                </Col>
                {(!formData.isNachtraege || uploadOnly) && (
                    <Col>
                        <TextFieldAtom
                            type="file"
                            label={
                                uploadOnly
                                    ? t("lv.upload-file")
                                    : t("lv.upload-gaeb-file")
                            }
                            accept=".x84,.x85,.x86,.xlsx,.xls"
                            placeholder={
                                uploadOnly
                                    ? t("lv.upload-file")
                                    : t("lv.upload-gaeb-file")
                            }
                            required={uploadOnly}
                            error={errors.file ? t(errors.file) : ""}
                            onChange={handleFileChange}
                        />
                    </Col>
                )}
            </Row>

            {/* Show Nachtrag-specific fields */}
            {!uploadOnly && (formData.isNachtraege || isEditMode) && (
                <>
                    <Row className="mb-3">
                        <Col>
                            <DropdownAtom
                                label={t("lv.status.title")}
                                placeholder={t("lv.select-status")}
                                options={statusOptions}
                                value={formData.statusCode}
                                required
                                error={errors.statusCode ? t(errors.statusCode) : ""}
                                onChange={(e) =>
                                    handleInputChange("statusCode", e.target.value)
                                }
                                disabled={loading}
                                sortOptions={false} // Keep original order of status options
                            />
                        </Col>
                        <Col>
                            <TextFieldAtom
                                type="number"
                                label={t("lv.cost")}
                                placeholder={t("lv.cost-placeholder")}
                                required
                                value={formData.cost}
                                error={errors.cost ? t(errors.cost) : ""}
                                onChange={(e) =>
                                    handleInputChange("cost", e.target.value)
                                }
                                disabled={loading}
                                min="0"
                                step="0.01"
                                className="text-end"
                                suffix="€"
                            />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <div className="alert alert-info mb-0">
                                <small>{t("lv.nachtrag-key-auto-generated")}</small>
                            </div>
                        </Col>
                    </Row>
                </>
            )}

            <Row className="mb-3">
                <Col>
                    <DropdownAtom
                        label={t("lv.ticket-category")}
                        placeholder={t("lv.ticket-category")}
                        options={ticketCategories}
                        value={formData.projectTicketCategoryId}
                        required={!uploadOnly}
                        loading={isLoadingCategories}
                        error={
                            errors.projectTicketCategoryId
                                ? t(errors.projectTicketCategoryId)
                                : ""
                        }
                        onChange={(e) =>
                            handleInputChange(
                                "projectTicketCategoryId",
                                e.target.value,
                            )
                        }
                        disabled={uploadOnly || loading}
                    />
                </Col>
            </Row>

            {uploadOnly && initialData && (
                <Row className="mb-3">
                    <Col>
                        <div className="alert alert-info">
                            {t("lv.upload-file-info", {
                                lvName: initialData.name,
                            })}
                        </div>
                    </Col>
                </Row>
            )}

            {!uploadOnly && !initialData && (
                <Row className="mb-3">
                    <Col>
                        <div className="alert alert-info">
                            {t("lv.ticket-category-list-info")}
                        </div>
                    </Col>
                </Row>
            )}

            {!hideButtons && (
                <Row>
                    <Col xs={12}>
                        <div className="d-flex gap-2 justify-content-end">
                            <ButtonAtom
                                className="btn btn-secondary"
                                onClick={() => onCancel()}
                                disabled={loading}
                            >
                                {t("common.cancel")}
                            </ButtonAtom>
                            <ButtonAtom
                                disabled={loading}
                                onClick={handleSubmit}
                            >
                                {isUploadMode ? t("lv.upload-file") : isEditMode ? t("common.update") : t("common.save")}
                            </ButtonAtom>
                        </div>
                    </Col>
                </Row>
            )}
        </Container>
    );
});

export default AddLVForm;
